import { type PackageUnitTypeEnum } from "@/__generated__/graphql";
import { useBookingRoute } from "@/components/hooks/use-booking-route";
import { BottomDrawerModal } from "@/components/shared/bottom-drawer-modal";
import { PawPrint } from "@/components/shared/icons";
import { ListHeading } from "@/components/shared/list-header";
import { Spin } from "@/components/shared/spin";
import { Button, RadioGroup, View } from "@/components/ui";
import { showToast } from "@/components/ui/toast/show-toast";
import { PetGroomingUnitTypeOptions, PetSittingUnitTypeOptions } from "@/constants/booking";
import { formatPrice } from "@/lib/helpers/number";
import { useBookingState } from "@/store/booking/useBookingState";

import { Typography } from "../../typography";
import { ServiceRadioCard } from "../service-radio-card";

export const ChoosePetCarePackageModal = () => {
  const { forward } = useBookingRoute();
  const {
    data: { packageDetailsFilter, choosePackageLoading, petCarePackageModalOpen },
    setBookingState,
  } = useBookingState();

  const isPetGrooming = petCarePackageModalOpen === "pet-grooming";

  const onSelect = (value: PackageUnitTypeEnum) => {
    setBookingState({
      packageDetailsFilter: {
        ...packageDetailsFilter,
        unitTypes: [value],
      },
    });
  };

  const onContinue = () => {
    if (!packageDetailsFilter?.unitTypes?.length) {
      return showToast({
        title: "Please choose your package!",
        type: "info",
      });
    }
    setBookingState({
      petCarePackageModalOpen: null,
    });
    forward();
  };

  const petCareOptions = isPetGrooming ? PetGroomingUnitTypeOptions : PetSittingUnitTypeOptions;

  const title = isPetGrooming
    ? "Let us know if we’ll be grooming your feline friend or your loyal pup."
    : "Tell us who we’re sitting your feline ruler or your tail-wagging sidekick.";

  const unitText = isPetGrooming ? "/session" : "";

  return (
    <BottomDrawerModal
      open={!!petCarePackageModalOpen}
      onOpenChange={() => {
        setBookingState({
          petCarePackageModalOpen: null,
        });
      }}
    >
      <View className="flex flex-col gap-4">
        <ListHeading title="Who’s getting pampered?" icon={PawPrint} />
        <Typography variant="body-md" color="foreground-intermediate">
          {title}
        </Typography>

        <View>
          {choosePackageLoading && <Spin className="my-12 size-12 shrink-0" />}
          <RadioGroup value={packageDetailsFilter.unitTypes?.[0]} onValueChange={() => {}}>
            {petCareOptions.map((option) => {
              return (
                <ServiceRadioCard
                  key={option.value}
                  active={packageDetailsFilter.unitTypes?.[0] === option.value}
                  onSelect={() => onSelect(option.value)}
                  imgUrl={option.imgUrl}
                  title={option.title}
                  value={option.value}
                  subTitle={`from ${formatPrice(option.price)}${unitText}`}
                  thirdTitle={option.caption ?? ""}
                  alignment="center"
                />
              );
            })}
          </RadioGroup>
        </View>

        <Button
          variant="primary"
          color="CTA"
          size="lg"
          rounded="full"
          onClick={onContinue}
          children="Continue"
        />
      </View>
    </BottomDrawerModal>
  );
};
